import { CsrAPI } from "../../apis/CsrAPI";
import { FILE_UPLOAD_STATUS } from "../../utils/Constants";
import {
  fetchFigureNames,
  fetchTableNames,
  resetForStudy,
  setStatusError,
  setStatusPolling,
  setStatusReady,
} from "../slices/csrDataSlice";

// Module-level singleton, NOT useRef. A useRef map lives per component
// instance and cannot dedupe across the tree — that was the bug in
// useTableStatusPolling.
const intervals = new Map(); // studyId -> { dispose }

// Tracks studies for which the load pipeline has been kicked off in this
// JS context. The polling singleton dedupes while polling is running, but
// the moment polling stops (ready detected) there's a window where a
// re-entrant call could still slip past the slice's condition guards —
// particularly under React Strict Mode, multiple wizard mount points, or
// HMR. This Set guarantees one-shot start per study lifetime.
const startedStudies = new Set();

const DEFAULT_POLL_INTERVAL_MS = 5000;
// Backend parsing can take minutes. A flat 5s interval means ~12 requests a
// minute per open tab for the whole wait, so each tick backs off until it
// reaches MAX_POLL_INTERVAL_MS. The backoff resets whenever the tab comes
// back to the foreground, so a returning user still gets a prompt answer.
const MAX_POLL_INTERVAL_MS = 30000;
const POLL_BACKOFF_FACTOR = 1.5;

// Mirrors the backend rule behind GET /studies/{study_id}/status:
// study_files is true only when every document is PROCESSED.
const PROCESSED_DOCUMENT_STATUS = FILE_UPLOAD_STATUS.processed;

const isCancellation = err =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

const canObserveVisibility = () =>
  typeof document !== "undefined" &&
  typeof document.addEventListener === "function";

const isTabHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

export const isPolling = studyId => intervals.has(studyId);

const startPollingInternal = (studyId, dispatch, getState, intervalMs) => {
  if (intervals.has(studyId)) return;

  // `stopped` is the closure flag the singleton flips when stopPolling is
  // called. In-flight checks observe it and bail out so we never apply
  // a stale tick after the page has navigated away. `fetchesDispatched`
  // guards against the cycle firing tables/figures more than once even if
  // an interval tick raced past `stopPolling`.
  let stopped = false;
  let fetchesDispatched = false;
  let timeoutId = null;
  let delay = intervalMs;
  let inFlight = false;

  const clearPending = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  // Self-scheduling chain instead of setInterval: the next tick is only
  // queued once the previous response has landed, so a slow backend can
  // never make requests pile up on top of each other.
  const schedule = () => {
    if (stopped || fetchesDispatched) return;
    clearPending();
    timeoutId = setTimeout(tick, delay);
  };

  const checkOnce = async () => {
    if (stopped || fetchesDispatched) return;
    try {
      const resp = await CsrAPI.getAllStatuses({ studyId }, true);
      if (stopped || fetchesDispatched) return;

      // Race guard: study changed mid-flight.
      if (getState().csrData.studyId !== studyId) {
        stopPolling(studyId);
        return;
      }

      const ready = resp?.study_files === true;
      if (ready) {
        fetchesDispatched = true;
        stopPolling(studyId);
        dispatch(setStatusReady());
        dispatch(fetchTableNames(studyId));
        dispatch(fetchFigureNames(studyId));
        return;
      }

      // Not ready yet, but the endpoint answered — clear a previous
      // transient failure so the page shows "still loading" rather than a
      // stale error for the rest of the wait.
      if (getState().csrData.status === "error") {
        dispatch(setStatusPolling());
      }
    } catch (err) {
      if (stopped || isCancellation(err)) return;
      if (getState().csrData.studyId !== studyId) return;
      dispatch(setStatusError(err?.message || "Failed to check status"));
      // Keep polling — transient backend errors shouldn't strand the page.
    }
  };

  const tick = async () => {
    timeoutId = null;
    if (stopped || fetchesDispatched) return;
    // A check is already running (e.g. the tab regained focus mid-request);
    // it will schedule the next one when it settles.
    if (inFlight) return;

    // Nobody is looking at the page — skip the request entirely and try
    // again later. `onVisibilityChange` fires an immediate check when the
    // user comes back, so nothing is delayed for them.
    if (isTabHidden()) {
      schedule();
      return;
    }

    inFlight = true;
    try {
      await checkOnce();
    } finally {
      inFlight = false;
    }
    schedule();
    // Bump only after scheduling, so the first re-check still happens at the
    // base interval and the wait grows from there.
    delay = Math.min(
      Math.round(delay * POLL_BACKOFF_FACTOR),
      MAX_POLL_INTERVAL_MS
    );
  };

  const onVisibilityChange = () => {
    if (stopped || fetchesDispatched || isTabHidden()) return;
    // Back in the foreground: reset the backoff and check straight away so
    // the user doesn't wait out a 30s interval that started while hidden.
    delay = intervalMs;
    clearPending();
    tick();
  };

  // Register the entry *before* dispatching anything, so that the first
  // `checkOnce` can correctly find and clear it via `stopPolling` if the
  // status response arrives synchronously / from cache.
  const entry = {
    dispose: () => {
      stopped = true;
      clearPending();
      if (canObserveVisibility()) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    },
  };
  intervals.set(studyId, entry);
  if (canObserveVisibility()) {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  dispatch(setStatusPolling());
  tick();
};

export const stopPolling = studyId => {
  const entry = intervals.get(studyId);
  if (!entry) return;
  entry.dispose();
  intervals.delete(studyId);
};

export const stopAllPolling = () => {
  intervals.forEach(entry => {
    entry.dispose();
  });
  intervals.clear();
  startedStudies.clear();
};

/**
 * Idempotent entry point. Dispatch from the page that owns the study
 * (the wizard questions page) to begin polling extraction status. Once
 * status flips ready, tables and figures are fetched once and hydrated
 * into the csrData slice. Safe to dispatch from multiple mount points;
 * the singleton guarantees a single interval per studyId.
 */
export const startStudyDataLoad =
  (studyId, options = {}) =>
  (dispatch, getState) => {
    if (!studyId) return;

    const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const slice = getState().csrData;

    if (slice.studyId && slice.studyId !== studyId) {
      // Tearing down the previous study cancels its polling and any
      // in-flight ticks via the closure flag. The previous study's
      // started-marker also goes — if the user comes back to it later,
      // a fresh load cycle is allowed.
      stopPolling(slice.studyId);
      startedStudies.delete(slice.studyId);
      dispatch(resetForStudy(studyId));
    } else if (slice.studyId == null) {
      dispatch(resetForStudy(studyId));
    }

    // Hard one-shot guard: if we've already initiated the pipeline for
    // this study in this JS context, do nothing — even if polling has
    // since finished and slice flags would otherwise allow a retry.
    if (startedStudies.has(studyId)) return;

    const refreshed = getState().csrData;

    // Polling already running — singleton dedupes.
    if (isPolling(studyId)) return;

    // Status flipped ready (fetches dispatched or done) or a fetch is
    // already in flight — don't kick off a fresh polling cycle that would
    // race the existing thunks. The thunks' own condition guards block
    // duplicates from there.
    if (
      refreshed.status === "ready" ||
      refreshed.tables.loading ||
      refreshed.figures.loading
    ) {
      startedStudies.add(studyId);
      return;
    }

    startedStudies.add(studyId);
    startPollingInternal(studyId, dispatch, getState, intervalMs);
  };

/**
 * Reconcile the loaded tables/figures with the real document state.
 *
 * GET /studies/{study_id}/documents is already fetched (and refreshed) by
 * CsrProvider. Any document that isn't PROCESSED means the backend's
 * /status endpoint is reporting study_files: false, so whatever tables and
 * figures we hold are stale — drop them and put the status poller back to
 * work.
 *
 * A no-op while a poll cycle is already running, so the recurring documents
 * refresh can never restart polling in a loop (a permanently ERRORed
 * document keeps the existing cycle going rather than spawning new ones).
 */
export const syncStudyDataWithDocuments =
  (studyId, documents, options = {}) =>
  (dispatch, getState) => {
    if (!studyId || !Array.isArray(documents) || documents.length === 0) return;

    const hasUnprocessedDocument = documents.some(
      doc => doc?.status !== PROCESSED_DOCUMENT_STATUS
    );
    if (!hasUnprocessedDocument) return;

    // Already polling this study: the store was reset when that cycle
    // started and the poller will pick the new document up on its next tick.
    if (isPolling(studyId)) return;

    const slice = getState().csrData;
    const alreadyIdle =
      slice.studyId === studyId &&
      slice.status === "idle" &&
      !slice.tables.fetched &&
      !slice.figures.fetched &&
      !slice.tables.loading &&
      !slice.figures.loading;

    if (!alreadyIdle) {
      dispatch(resetForStudy(studyId));
    }

    // Clear the one-shot marker so the pipeline is allowed to run again for
    // this study, then hand back to the normal entry point.
    startedStudies.delete(studyId);
    dispatch(startStudyDataLoad(studyId, options));
  };
